import { Fragment, useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import ButtonComponent from '../../../components/stories/atoms/button'
import {
  buttonSizeOptions,
  buttonTypeOptions,
  buttonVariantOptions
} from '../../../components/stories/atoms/button/constants'
import { Context } from '../../../components/stories/atoms/icons/essentials'
import colorsOptions from '../../../components/stories/fundamentals/colors-styles/constants'
import ContextMenuComponent from '../../../components/stories/organisms/context-menu'
import {
  contextMenuAlignOptions,
  contextMenuArrowAlignmentOptions,
  contextMenuPaddingOptions,
  contextMenuSideOptions,
  contextMenuTitleSizeOptions,
  contextMenuTitleWeightOptions,
  contextMenuVariantOptions
} from '../../../components/stories/organisms/context-menu/constants'
import { useUpdatePlanDetailsMutation } from '../../../react-query/plan/mutation/use-update-plan-details-mutation'
import { planTreeItemTypeOptions } from '../../../react-query/plan/query/use-get-plan-tree-query'
import { planPermissionOptions, renamePlanOptions } from '../constants'
import { useIntl } from 'react-intl'
import PropTypes from 'prop-types'
import { EllipseBold } from '../../../components/stories/atoms/icons/image-overlay'
import DeactivatePlanAlert from './deactivate-plan-alert'
import DeleteFolderAlert from '../../../components/delete-folder-alert'
import { useSnackbar } from '../../../components/stories/molecules/snackbar/component/snackbar-hook'
import { snackbarVariantOptions } from '../../../components/stories/molecules/snackbar/constants'
import { useDeletePlanFolderMutation } from '../../../react-query/plan/mutation/use-delete-plan-folder-mutation'
import { useHistory, useParams } from 'react-router-dom'
import { usePlanMenuListViewControl } from '../../../hooks/use-plan-menu-list-view-control'
import PlanMenuContextMenuFeatureItem from './plan-menu-context-menu-feature-item'
import { useMenuTriggerRect } from '../../../hooks/use-menu-trigger-rect'
import { useGetProjectDetailsQuery } from '../../../react-query/project/query/use-get-project-details-query'

const MENU_ESTIMATED_HEIGHT = 250
const MENU_WIDTH = 200
const MENU_TRIGGER_GAP = 4
const MENU_VIEWPORT_MARGIN = 8

// In the table the menu opens to the side of the trigger, vertically centred on
// it (per design); the tile view keeps it under the trigger. menuHeight is 0 on
// the first render, before the menu is measured — the translate keeps it centred
// meanwhile, and only the off-screen clamp needs the real height.
const getMenuPlacement = ({ triggerRect, menuHeight, isTileView }) => {
  if (isTileView) {
    const openUpward =
      window.innerHeight - triggerRect.bottom < MENU_ESTIMATED_HEIGHT

    return {
      side: openUpward
        ? contextMenuSideOptions.TOP
        : contextMenuSideOptions.BOTTOM,
      align: contextMenuAlignOptions.START,
      arrowAlignment: contextMenuArrowAlignmentOptions.END,
      style: {
        top: openUpward ? 'auto' : triggerRect.bottom,
        bottom: openUpward ? window.innerHeight - triggerRect.top : 'auto',
        right: window.innerWidth - triggerRect.right,
        left: 'auto',
        transform: 'none'
      }
    }
  }

  const openToLeft =
    triggerRect.left >= MENU_WIDTH + MENU_TRIGGER_GAP + MENU_VIEWPORT_MARGIN

  const centerY = triggerRect.top + triggerRect.height / 2
  const halfHeight = menuHeight / 2
  const top = menuHeight
    ? Math.min(
        Math.max(centerY, MENU_VIEWPORT_MARGIN + halfHeight),
        window.innerHeight - MENU_VIEWPORT_MARGIN - halfHeight
      )
    : centerY

  return {
    side: openToLeft
      ? contextMenuSideOptions.LEFT
      : contextMenuSideOptions.RIGHT,
    align: contextMenuAlignOptions.CENTER,
    arrowAlignment: contextMenuArrowAlignmentOptions.CENTER,
    style: {
      top,
      bottom: 'auto',
      right: openToLeft
        ? window.innerWidth - triggerRect.left + MENU_TRIGGER_GAP
        : 'auto',
      left: openToLeft ? 'auto' : triggerRect.right + MENU_TRIGGER_GAP,
      transform: 'translateY(-50%)'
    }
  }
}

const getContextMenuItems = ({
  formatMessage,
  item,
  projectPermissions,

  // plan handlers
  onImportRevision,
  onManageVersions,
  onRenameItem,
  onMoveItem,
  onExportItem,
  onTogglePlanActive,

  // folder handlers
  onImportPlan,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder
}) => {
  const isActive = item.active
  const isPlan = item.type === planTreeItemTypeOptions.PLAN

  // Gated by the user's permissions in this project (its role), not by the
  // organisation admin flag: a project Administrator who is not an org admin
  // must still manage plans.
  const canCreatePlan = projectPermissions.includes(
    planPermissionOptions.CREATE_PLAN
  )
  const canManageFolders = projectPermissions.includes(
    planPermissionOptions.CREATE_PLAN_FOLDER
  )
  const canEditPlan = !!item.permissions?.includes(planPermissionOptions.EDIT)
  const canRename = isPlan ? canEditPlan : canManageFolders

  return [
    {
      id: 'import-plan',
      title: formatMessage({ id: 'plans_action_import' }),
      action: () => onImportPlan(item),
      hide: !isActive || isPlan || !canCreatePlan,
      showDivider: false
    },
    {
      id: 'import-revision',
      title: formatMessage({ id: 'plans_action_import-revision' }),
      action: () => onImportRevision(item),
      hide: !isActive || !isPlan || !canCreatePlan || !canEditPlan,
      showDivider: false
    },
    {
      id: 'new-folder',
      title: formatMessage({ id: 'projects_newFolder' }),
      action: () => onCreateFolder(item),
      hide: !isActive || isPlan || !canManageFolders,
      showDivider: true
    },
    {
      id: 'manage-revision',
      title: formatMessage({ id: 'plans_action_manage-revision' }),
      action: () => onManageVersions(item),
      hide: !isActive || !isPlan,
      showDivider: true
    },
    {
      id: 'rename',
      title: formatMessage({ id: 'rename' }),
      action: () => onRenameItem(item),
      hide: !isActive || !canRename,
      showDivider: false
    },
    {
      id: 'move',
      title: formatMessage({ id: 'move' }),
      action: () => onMoveItem(item),
      hide: !isActive || !canManageFolders,
      showDivider: true
    },
    {
      id: 'export',
      title: formatMessage({ id: 'export' }),
      action: () => onExportItem(item),
      featureSlug: 'PLAN_EXPORT',
      hide: !isPlan || !isActive,
      showDivider: false
    },
    {
      id: 'delete',
      title: formatMessage({ id: 'delete' }),
      action: () => onDeleteFolder(item),
      hide: !isActive || isPlan || !canManageFolders,
      disabled: item.items > 0,
      variant: contextMenuVariantOptions.DESTRUCTIVE,
      showDivider: false
    },
    {
      id: 'plan-active-toggle',
      // Reactivation reuses the multi-select key so both menus share one copy;
      // this menu always acts on a single plan.
      title: isActive
        ? formatMessage({ id: 'deactivate' })
        : formatMessage({ id: 'plans_action_reactivate' }, { count: 1 }),
      action: () => onTogglePlanActive(item),
      hide: !isPlan || !canCreatePlan,
      variant: isActive
        ? contextMenuVariantOptions.DESTRUCTIVE
        : contextMenuVariantOptions.STANDARD,
      showDivider: false
    }
  ].filter(({ hide }) => !hide)
}

const PlanMenuContextMenu = ({ item, isTileView = false }) => {
  const { formatMessage } = useIntl()
  const [isOpen, setIsOpen] = useState(false)
  const [planToDeactivate, setPlanToDeactivate] = useState(null)
  const [isDeleteFolderAlertOpen, setIsDeleteFolderAlertOpen] = useState(false)
  const [menuHeight, setMenuHeight] = useState(0)
  const { addSnackbar } = useSnackbar()
  const history = useHistory()
  const { projectId } = useParams()

  const { viewType } = usePlanMenuListViewControl()
  const { data: projectDetails } = useGetProjectDetailsQuery({ projectId })

  const iconColor = item.active
    ? colorsOptions.greys['grey-90']
    : colorsOptions.greys['grey-40']

  const showSnackbarFolderDeletedSuccess = () => {
    addSnackbar({
      message: formatMessage({ id: 'folder_deleted' }, { count: 1 }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  const showSnackbarPlanToggleIsActiveSuccess = (data) => {
    const { active } = data

    const message = active
      ? 'plans_message_reactivate-success'
      : 'plans_message_deactivate-success'

    addSnackbar({
      // The new copy is pluralised, and this menu always acts on a single plan.
      message: formatMessage({ id: message }, { count: 1 }),
      snackbarType: snackbarVariantOptions.SUCCESS,
      hideCloseButton: true
    })
  }

  // mutates

  const { mutate: deleteFolder, isPending: deleteFolderLoading } =
    useDeletePlanFolderMutation({
      onSuccess: () => {
        setIsDeleteFolderAlertOpen(false)
        showSnackbarFolderDeletedSuccess()
      }
    })

  const { mutate: updatePlan, isPending: updatePlanLoading } =
    useUpdatePlanDetailsMutation({
      onSuccess: showSnackbarPlanToggleIsActiveSuccess
    })

  // handlers
  const handleTriggerClick = useCallback((event) => {
    event.stopPropagation()
  }, [])

  // measured when the menu mounts, reset to 0 when it unmounts
  const handleMenuRef = useCallback((node) => {
    setMenuHeight(node ? node.getBoundingClientRect().height : 0)
  }, [])

  const handleItemClick = (event, action, disabled) => {
    event.stopPropagation()

    if (disabled) return

    setIsOpen(false)
    action()
  }

  const handleCloseInactiveAlert = (e) => {
    e.stopPropagation()
    setPlanToDeactivate(null)
  }

  const handleOpenDeleteFolderAlert = () => {
    setIsDeleteFolderAlertOpen(true)
  }

  const handleDeleteFolder = () => {
    deleteFolder({ folderId: item.id })
  }

  const handleImportRevision = (item) => {
    const planId = item.id

    history.push(
      `/project/${projectId}/importRevision/${planId}?view=${viewType}`
    )
  }

  const handleImportPlan = (item) => {
    const targetFolder = item.id
    history.push(
      `/project/${projectId}/import/${targetFolder}?view=${viewType}`
    )
  }

  const handleManageVersions = (item) => {
    const planId = item.id

    history.push(
      `/project/${projectId}/planVersions/${planId}?view=${viewType}`
    )
  }

  const handleCreateFolder = (item) => {
    const targetFolder = item.id

    history.push(
      `/project/${projectId}/newfolder/${targetFolder}?view=${viewType}`
    )
  }

  const handleMoveItem = (item) => {
    const planId = item.id
    const type =
      item.type === planTreeItemTypeOptions.FOLDER
        ? renamePlanOptions.FOLDER
        : renamePlanOptions.PLAN

    history.push(
      `/project/${projectId}/move/${planId}?type=${type}&view=${viewType}`
    )
  }

  const handleRenameItem = (item) => {
    const itemId = item.id
    const type =
      item.type === planTreeItemTypeOptions.FOLDER
        ? renamePlanOptions.FOLDER
        : renamePlanOptions.PLAN

    history.push(
      `/project/${projectId}/rename/${itemId}?type=${type}&view=${viewType}`
    )
  }

  const handleExportPlan = (item) => {
    const planId = item.id

    // carry the plan's open-pin count so the export modal can show it without
    // refetching the tree; it falls back gracefully on a hard refresh.
    history.push({
      pathname: `/project/${projectId}/exportPlan/${planId}`,
      search: `?view=${viewType}`,
      state: { openPins: item.openPins }
    })
  }

  const handleUpdatePlan = (plan, active) => {
    updatePlan({
      planId: plan.id,
      planDetails: { active }
    })
  }

  // open deactivate alert or activate plan
  const handleTogglePlanActive = (plan) => {
    if (plan.active) {
      setPlanToDeactivate(plan)
      return
    }

    handleUpdatePlan(plan, true)
  }

  const handleSetPlanInactive = (e) => {
    e.stopPropagation()
    handleUpdatePlan(planToDeactivate, false)
  }

  // handleX handlers above are plain functions recreated every render, so
  // memoizing this with useMemo would never hit its cache — just derive it.
  const menuItems = getContextMenuItems({
    formatMessage,
    item,
    projectPermissions: projectDetails?.permissions ?? [],

    // plan handlers
    onImportRevision: handleImportRevision,
    onManageVersions: handleManageVersions,
    onExportItem: handleExportPlan,
    onTogglePlanActive: handleTogglePlanActive,

    // folder handlers
    onImportPlan: handleImportPlan,
    onCreateFolder: handleCreateFolder,
    onDeleteFolder: handleOpenDeleteFolderAlert,

    // folder/plan handlers
    onRenameItem: handleRenameItem,
    onMoveItem: handleMoveItem
  })

  const tableTriggerButton = (
    <ButtonComponent
      variant={buttonVariantOptions.GHOST}
      buttonType={buttonTypeOptions['ICON-ONLY']}
      disabled={!menuItems.length}
    >
      <Context
        style={{
          fill: iconColor,
          stroke: iconColor,
          strokeWidth: 0
        }}
      />
    </ButtonComponent>
  )

  const tilesTriggerButton = (
    <ButtonComponent
      buttonType={buttonTypeOptions['ICON-ONLY']}
      variant={buttonVariantOptions['IMAGE-OVERLAY']}
      size={buttonSizeOptions.SMALL}
    >
      <EllipseBold stroke={colorsOptions.greys.white} />
    </ButtonComponent>
  )

  const triggerRef = useRef(null)
  const triggerRect = useMenuTriggerRect(triggerRef, isOpen)
  const defineTrigger = isTileView ? tilesTriggerButton : tableTriggerButton

  if (!menuItems.length) {
    return defineTrigger
  }

  const placement = triggerRect
    ? getMenuPlacement({ triggerRect, menuHeight, isTileView })
    : null

  return (
    <>
      <div ref={triggerRef}>
        <ContextMenuComponent.Root isOpen={isOpen} setIsOpen={setIsOpen}>
          <ContextMenuComponent.Trigger callback={handleTriggerClick}>
            {defineTrigger}
          </ContextMenuComponent.Trigger>
          {triggerRect &&
            createPortal(
              // the wrapper carries the positioning so its box can be measured:
              // the list itself is taken out of the flow by its own styles
              <div
                ref={handleMenuRef}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  position: 'fixed',
                  zIndex: 9999,
                  ...placement.style
                }}
              >
                <ContextMenuComponent.List
                  width={`${MENU_WIDTH}px`}
                  align={placement.align}
                  side={placement.side}
                  arrow
                  arrowAlignment={placement.arrowAlignment}
                  style={{
                    position: 'relative',
                    top: 'auto',
                    bottom: 'auto',
                    left: 'auto',
                    right: 'auto',
                    transform: 'none'
                  }}
                >
                  {menuItems.map(
                    ({
                      action,
                      id,
                      title,
                      variant,
                      showDivider = false,
                      disabled,
                      featureSlug
                    }) => {
                      const itemProps = {
                        id,
                        variant,
                        padding: contextMenuPaddingOptions['EXTRA-LARGE'],
                        titleWeight: contextMenuTitleWeightOptions['TEXT-600'],
                        titleSize: contextMenuTitleSizeOptions['TEXT-R'],
                        onClick: (e) => handleItemClick(e, action, disabled),
                        title,
                        disabled
                      }

                      return (
                        <Fragment key={id}>
                          {featureSlug ? (
                            <PlanMenuContextMenuFeatureItem
                              featureSlug={featureSlug}
                              {...itemProps}
                            />
                          ) : (
                            <ContextMenuComponent.Item {...itemProps} />
                          )}
                          {showDivider && <ContextMenuComponent.Divider />}
                        </Fragment>
                      )
                    }
                  )}
                </ContextMenuComponent.List>
              </div>,
              document.body
            )}
        </ContextMenuComponent.Root>
      </div>

      {planToDeactivate && (
        <DeactivatePlanAlert
          onClose={handleCloseInactiveAlert}
          onConfirm={handleSetPlanInactive}
          isLoading={updatePlanLoading}
        />
      )}

      <DeleteFolderAlert
        isOpen={isDeleteFolderAlertOpen}
        onClose={() => setIsDeleteFolderAlertOpen(false)}
        onDelete={handleDeleteFolder}
        onDeleteLoading={deleteFolderLoading}
      />
    </>
  )
}

PlanMenuContextMenu.propTypes = {
  item: PropTypes.object,
  isTileView: PropTypes.bool
}

export default PlanMenuContextMenu
